/**
 * Evolution of the estimated NCLC per skill, one point per graded sitting (plain SVG, tokens
 * only). Identity is never colour alone: each skill has its own marker and dash, a legend, a
 * direct label on the last point, and the full data table is available below the chart.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { bandText } from "./ielts/band";
import type { EvolutionPoint } from "./progressTypes";
import { dateText } from "./shared";
import { SKILLS, type Skill } from "./types";

const HEIGHT = 260;
const PAD = { top: 18, right: 56, bottom: 30, left: 28 };
const LABEL_GAP = 14;

/** Width of the frame in CSS pixels: the SVG is drawn 1:1, so its text stays readable on phones. */
function useFrameWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      if (next > 0) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

interface DirectLabel {
  text: string;
  x: number;
  y: number;
}

/** Last-point labels: equal values share one label, the others are pushed apart (no overlap). */
function placeLabels(labels: DirectLabel[], top: number, bottom: number): DirectLabel[] {
  const merged: DirectLabel[] = [];
  for (const label of [...labels].sort((a, b) => a.y - b.y)) {
    const same = merged.find((m) => Math.abs(m.y - label.y) < 1 && Math.abs(m.x - label.x) < 1);
    if (same) same.text = `${same.text} · ${label.text}`;
    else merged.push({ ...label });
  }
  for (let i = 1; i < merged.length; i += 1) {
    const prev = merged[i - 1];
    const cur = merged[i];
    if (prev && cur && cur.y - prev.y < LABEL_GAP) cur.y = prev.y + LABEL_GAP;
  }
  const last = merged[merged.length - 1];
  if (last && last.y > bottom) {
    const shift = last.y - bottom;
    for (const label of merged) label.y = Math.max(top, label.y - shift);
  }
  return merged;
}

/** A smooth line through the points (Catmull-Rom as cubic Béziers, no overshoot on the ends). */
function smoothPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return "";
  const first = points[0];
  if (!first) return "";
  let d = `M${first.x},${first.y}`;
  for (let i = 1; i < points.length; i += 1) {
    const p0 = points[i - 2] ?? points[i - 1];
    const p1 = points[i - 1];
    const p2 = points[i];
    const p3 = points[i + 1] ?? p2;
    if (!p0 || !p1 || !p2 || !p3) continue;
    const k = 0.18;
    const c1x = p1.x + (p2.x - p0.x) * k;
    const c1y = p1.y + (p2.y - p0.y) * k;
    const c2x = p2.x - (p3.x - p1.x) * k;
    const c2y = p2.y - (p3.y - p1.y) * k;
    d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
  }
  return d;
}

/** « 12/09 » under each sitting. */
function shortDate(value: string, language: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(language.startsWith("en") ? "en-GB" : "fr-FR", { day: "2-digit", month: "2-digit", timeZone: "Africa/Douala" });
}

function Marker({ skill, x, y }: { skill: Skill; x: number; y: number }) {
  const r = 4.5;
  if (skill === "ce") return <rect x={x - r} y={y - r} width={r * 2} height={r * 2} rx={1.5} />;
  if (skill === "ee") return <path d={`M${x} ${y - r - 1} L${x + r + 1} ${y + r} L${x - r - 1} ${y + r} Z`} />;
  if (skill === "eo") return <path d={`M${x} ${y - r - 1} L${x + r + 1} ${y} L${x} ${y + r + 1} L${x - r - 1} ${y} Z`} />;
  return <circle cx={x} cy={y} r={r} />;
}

/** `unit="band"`: the IELTS bands (0 to 9, the `nclc` field of each point then holds the band). */
export function EvolutionChart({ points, target, unit = "nclc" }: { points: EvolutionPoint[]; target: number; unit?: "nclc" | "band" }) {
  const { t, i18n } = useTranslation();
  const band = unit === "band";
  const step = band ? 1 : 2;
  const valueText = (n: number, cefr: string) =>
    band ? t("ielts.results.bandShort", { band: bandText(n), cefr }) : t("academy.results.nclcShort", { nclc: n, cefr });
  const skillShort = (skill: Skill) => t(band ? `ielts.skillShort.${skill}` : `academy.skillShort.${skill}`);
  const [hover, setHover] = useState<number | null>(null);
  const { ref, width: WIDTH } = useFrameWidth(640);
  const values = points.flatMap((p) => SKILLS.map((s) => p.skills[s]?.nclc ?? null)).filter((n): n is number => n !== null);
  const yMax = band
    ? Math.min(9, Math.ceil(Math.max(target, ...values) + 0.5))
    : Math.max(4, Math.ceil((Math.max(target, ...values) + 1) / 2) * 2);
  // Start the scale just under the lowest value (an even number): the changes stay readable.
  const low = Math.min(target, ...values);
  const yMin = band ? Math.max(0, Math.floor(low - 0.5)) : Math.max(0, Math.floor((low - 1) / 2) * 2);
  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (points.length > 1 ? (i / (points.length - 1)) * plotW : plotW / 2);
  const y = (n: number) => PAD.top + plotH - ((n - yMin) / (yMax - yMin || 1)) * plotH;
  const ticks = Array.from({ length: Math.floor((yMax - yMin) / step) + 1 }, (_, i) => yMin + i * step);
  const shown = SKILLS.filter((s) => points.some((p) => p.skills[s]?.nclc !== null && p.skills[s] !== undefined));
  const label = (p: EvolutionPoint) =>
    `${p.kind === "mock" && p.mock ? t("academy.progress.mockN", { n: p.mock }) : t(`academy.kind.${p.kind}`, { defaultValue: p.kind })} · ${dateText(p.at, i18n.language)}`;

  if (!points.length) return <p className="ac-secondary">{t("academy.progress.noEvolution")}</p>;

  const hovered = hover !== null ? points[hover] : undefined;
  const directLabels = placeLabels(
    shown.flatMap((skill) => {
      const values = points.map((p) => p.skills[skill]?.nclc ?? null);
      let index = values.length - 1;
      while (index >= 0 && values[index] === null) index -= 1;
      const n = index >= 0 ? points[index]?.skills[skill]?.nclc : null;
      return typeof n === "number" ? [{ text: skillShort(skill), x: x(index) + 10, y: y(n) }] : [];
    }),
    PAD.top,
    PAD.top + plotH,
  );

  return (
    <figure className="ac-chart">
      <div className="ac-chart__frame" ref={ref}>
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="img"
          aria-label={t("academy.progress.chartLabel", { count: points.length })}
          className="ac-chart__svg"
          onMouseLeave={() => setHover(null)}
        >
          {ticks.map((tick) => (
            <g key={tick} className="ac-chart__grid">
              <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(tick)} y2={y(tick)} />
              <text x={PAD.left - 8} y={y(tick)} dy="0.32em" textAnchor="end">
                {tick}
              </text>
            </g>
          ))}
          {points.map((p, i) =>
            points.length <= 8 || i % Math.ceil(points.length / 8) === 0 || i === points.length - 1 ? (
              <text key={`x-${p.attempt || i}`} className="ac-chart__axis" x={x(i)} y={HEIGHT - 8} textAnchor="middle">
                {shortDate(p.at, i18n.language)}
              </text>
            ) : null,
          )}
          <g className="ac-chart__target">
            <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(target)} y2={y(target)} />
            <text x={PAD.left + 6} y={y(target) - 7}>
              {band ? t("ielts.progress.targetShort", { band: bandText(target) }) : t("academy.progress.targetShort", { n: target })}
            </text>
          </g>
          {hover !== null && (
            <line className="ac-chart__cross" x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} />
          )}
          {shown.map((skill) => {
            const series = points
              .map((p, i) => ({ i, n: p.skills[skill]?.nclc ?? null }))
              .filter((p): p is { i: number; n: number } => p.n !== null);
            return (
              <g key={skill} className={`ac-series ac-skill--${skill} ac-series--${skill}`}>
                <path className="ac-series__line" d={smoothPath(series.map((p) => ({ x: x(p.i), y: y(p.n) })))} />
                {series.map((p) => (
                  <g key={p.i} className={`ac-series__mark${hover === p.i ? " ac-series__mark--on" : ""}`}>
                    <Marker skill={skill} x={x(p.i)} y={y(p.n)} />
                  </g>
                ))}
              </g>
            );
          })}
          {directLabels.map((label) => (
            <text key={label.text} className="ac-chart__direct" x={label.x} y={label.y} dy="0.32em">
              {label.text}
            </text>
          ))}
          {points.map((p, i) => (
            <rect
              key={p.attempt || i}
              className="ac-chart__hit"
              x={x(i) - (points.length > 1 ? plotW / (points.length - 1) / 2 : plotW / 2)}
              y={PAD.top}
              width={points.length > 1 ? plotW / (points.length - 1) : plotW}
              height={plotH}
              onMouseEnter={() => setHover(i)}
              onClick={() => setHover(i)}
            />
          ))}
        </svg>
        {hovered && hover !== null && (
          <div
            className="ac-chart__tip"
            role="status"
            style={{ left: `${(x(hover) / WIDTH) * 100}%` }}
          >
            <strong>{label(hovered)}</strong>
            {SKILLS.filter((s) => hovered.skills[s]).map((s) => (
              <span key={s}>
                {skillShort(s)} · {valueText(hovered.skills[s]?.nclc ?? 0, hovered.skills[s]?.cefr ?? "")}
              </span>
            ))}
          </div>
        )}
      </div>
      <figcaption>
        <ul className="ac-chart__legend">
          {shown.map((skill) => (
            <li key={skill} className={`ac-series ac-skill--${skill} ac-series--${skill}`}>
              <svg width="26" height="14" aria-hidden>
                <line className="ac-series__line" x1="1" x2="25" y1="7" y2="7" />
                <g className="ac-series__mark">
                  <Marker skill={skill} x={13} y={7} />
                </g>
              </svg>
              {t(band ? `ielts.skill.${skill}` : `academy.skill.${skill}`)}
            </li>
          ))}
          <li className="ac-chart__target">
            <svg width="26" height="14" aria-hidden>
              <line x1="1" x2="25" y1="7" y2="7" />
            </svg>
            {band ? t("ielts.progress.targetLine", { band: bandText(target) }) : t("academy.progress.targetLine", { n: target })}
          </li>
        </ul>
        <p className="ac-footnote">{band ? t("ielts.progress.chartEstimate") : t("academy.progress.chartEstimate")}</p>
      </figcaption>
      <details>
        <summary>{t("academy.progress.table")}</summary>
        <div className="ac-table-wrap">
          <table className="ac-data-table">
            <caption className="visually-hidden">{t("academy.progress.table")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("academy.progress.sitting")}</th>
                {SKILLS.map((s) => (
                  <th key={s} scope="col" className="num">
                    {skillShort(s)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {points.map((p, i) => (
                <tr key={p.attempt || i}>
                  <th scope="row">{label(p)}</th>
                  {SKILLS.map((s) => {
                    const cell = p.skills[s];
                    return (
                      <td key={s} className="num">
                        {cell && cell.nclc !== null ? valueText(cell.nclc, cell.cefr) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}
